/**
 * @file Labs linked-Patient lookup and display UI.
 */

import { appData } from "../../app/state/state.js";
import {
  escapeHTML,
  sanitizeHTML,
} from "../../shared/utils/contentUtils.js";
import {
  FIELD_WRAPPER_CLASSES,
  NATIVE_CHECKBOX_CLASSES,
  NATIVE_CHECKBOX_ICON_CLASSES,
  NATIVE_CHECKBOX_LABEL_CLASSES,
  NATIVE_CHECKBOX_MARKER_CLASSES,
  NATIVE_INPUT_CLASSES,
  bindNativeFieldBehavior,
  getFieldLabelHtml,
  getReadOnlyValueHtml,
} from "../../shared/forms/nativeFieldStyles.js";
import {
  attachLinkedParentFieldListeners,
  LINKED_PARENT_ACTIONS,
} from "../../shared/details/linkedParentSection.js";
import {
  lookupLabsPatientCandidates,
} from "./labsApi.js";

const MODAL_ID = "labs-patient-lookup-modal";
const RESULT_LIMIT = 20;
const CREATE_SEARCH_LIMIT = 8;
const PATIENT_LOOKUP_MIN_CHARS = 2;

function attrValue(value = "") {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function getPatientTypeConfig() {
  return appData?.config?.patientTypes || [];
}

function getPatientTypeName(typeId) {
  return getPatientTypeConfig().find((entry) => entry.id === typeId)?.name ||
    typeId;
}

function getKnownPatient(patientId) {
  if (!patientId) {return null;}
  return (appData?.patients || []).find((patient) => patient.id === patientId) ||
    null;
}

/**
 * Resolves the safest available display data for a linked Patient.
 * @param {object} source Lab item/form state.
 * @returns {object} Display data.
 */
export function getLabsPatientDisplay(source = {}) {
  const patientId = source?.linked_patient_id || "";
  const patient = getKnownPatient(patientId);
  return {
    id: patientId,
    name: patient?.name || source?.patient_name || "",
    caseFileNumber: patient?.case_file_number || source?.case_file_number || "",
    cardId: patient?.card_id || source?.card_id || "",
    types: Array.isArray(patient?.types) ?
      patient.types :
      (Array.isArray(source?.linked_patient_types) ?
        source.linked_patient_types :
        []),
    provider: Array.isArray(patient?.provider) ?
      patient.provider :
      (Array.isArray(source?.provider) ? source.provider : []),
  };
}

function renderLinkActionButton({
  action,
  ariaLabel,
  active = false,
  icon,
  patientId = "",
  title,
  tone = "neutral",
}) {
  const toneClasses = tone === "danger" ?
    "hover:text-red-600 focus:text-red-600" :
    `${active ? "text-[var(--dfc-brand-primary)]" : "text-gray-400"} hover:text-[var(--dfc-brand-primary)] focus:text-[var(--dfc-brand-primary)]`;
  return `
    <button
      type="button"
      class="inline-flex h-6 w-6 items-center justify-center rounded-full transition-colors ${toneClasses} focus:outline-none"
      data-linked-parent-action="${attrValue(action)}"
      data-linked-parent-field="labs-linked-patient"
      data-linked-parent-id="${attrValue(patientId)}"
      data-linked-parent-type="patient"
      title="${attrValue(title)}"
      aria-label="${attrValue(ariaLabel)}"
    >
      <span class="material-symbols-outlined pointer-events-none text-base leading-none">${icon}</span>
    </button>
  `;
}

/**
 * Renders the linked Patient field used by Labs create/edit surfaces.
 * @param {object} params Field params.
 * @param {object} params.source Lab item/form state.
 * @param {boolean} [params.canLink=true] Whether link/change is enabled.
 * @param {boolean} [params.canUnlink=true] Whether unlink is enabled.
 * @returns {string} Field HTML.
 */
export function renderLabsPatientLinkField({
  source = {},
  canLink = true,
  canUnlink = true,
} = {}) {
  const display = getLabsPatientDisplay(source);
  const hasLink = Boolean(display.id);

  if (!hasLink) {
    return `
      <div class="md:col-span-2 grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4" data-linked-parent-field="labs-linked-patient">
        <input type="hidden" name="linked_patient_id" value="">
        <div class="${FIELD_WRAPPER_CLASSES}">
          ${getFieldLabelHtml("Patient Name", true)}
          <input
            type="text"
            name="patient_name"
            value="${attrValue(source.patient_name || "")}"
            class="${NATIVE_INPUT_CLASSES}"
            required
          >
        </div>
        <div class="flex min-w-0 items-end">
          <button
            type="button"
            class="inline-flex min-h-10 w-full items-center justify-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-100 focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)] sm:min-h-[30px]"
            data-linked-parent-action="${attrValue(LINKED_PARENT_ACTIONS.LINK)}"
            data-linked-parent-field="labs-linked-patient"
            data-linked-parent-id=""
            data-linked-parent-type="patient"
            ${canLink ? "" : "disabled"}
          >
            <span class="material-symbols-outlined text-[18px] leading-none">link</span>
            Link patient
          </button>
        </div>
      </div>
    `;
  }

  return `
    <div class="md:col-span-2 grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4" data-linked-parent-field="labs-linked-patient">
      <input type="hidden" name="linked_patient_id" value="${attrValue(display.id)}">
      <input type="hidden" name="patient_name" value="${attrValue(display.name)}">
      <div class="${FIELD_WRAPPER_CLASSES}">
        ${getFieldLabelHtml("Patient", true)}
        ${getReadOnlyValueHtml(sanitizeHTML(display.name || "Linked patient"))}
      </div>
      <div class="flex min-w-0 items-start justify-start gap-0.5 md:pt-6">
        ${canLink ? renderLinkActionButton({
          action: LINKED_PARENT_ACTIONS.LINK,
          ariaLabel: "Change linked patient",
          active: true,
          icon: "link",
          patientId: display.id,
          title: "Change linked patient",
        }) : ""}
        ${canUnlink ? renderLinkActionButton({
          action: LINKED_PARENT_ACTIONS.UNLINK,
          ariaLabel: "Unlink patient",
          icon: "link_off",
          patientId: display.id,
          title: "Unlink patient",
          tone: "danger",
        }) : ""}
      </div>
    </div>
  `;
}

function ensureModal() {
  let modal = document.getElementById(MODAL_ID);
  if (modal) {return modal;}

  modal = document.createElement("div");
  modal.id = MODAL_ID;
  modal.className = "fixed inset-0 z-50 hidden items-center justify-center bg-black/40 p-4";
  modal.innerHTML = `
    <div class="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
      <div class="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
        <h2 class="text-lg font-semibold text-gray-900">Link Patient</h2>
        <button type="button" class="labs-patient-lookup-close standard-action-icon-btn standard-action-icon-btn--danger shrink-0" aria-label="Close patient lookup">
          <span class="material-symbols-outlined text-[20px] leading-none">close</span>
        </button>
      </div>
      <div class="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
        <form data-labs-patient-search-form>
          <label class="min-w-0">
            <span class="mb-1 block text-sm font-medium text-gray-700">Search Patients</span>
            <input
              type="search"
              name="patientSearch"
              class="${NATIVE_INPUT_CLASSES}"
              placeholder="Name, case file, or card ID"
              autocomplete="off"
            >
          </label>
        </form>
        <div data-labs-patient-type-filters></div>
        <div data-labs-patient-message class="hidden rounded-md border px-3 py-2 text-sm"></div>
        <div data-labs-patient-results class="space-y-2"></div>
      </div>
      <div class="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
        <button type="button" class="labs-patient-lookup-close inline-flex h-9 items-center justify-center rounded-md bg-gray-200 px-4 text-sm font-semibold text-gray-800 hover:bg-gray-300">
          Cancel
        </button>
      </div>
    </div>
  `;
  document.body.append(modal);
  return modal;
}

function setModalMessage(modal, message, tone = "neutral") {
  const element = modal.querySelector("[data-labs-patient-message]");
  if (!element) {return;}

  if (!message) {
    element.className = "hidden rounded-md border px-3 py-2 text-sm";
    element.textContent = "";
    return;
  }

  const toneClasses = tone === "error" ?
    "border-red-200 bg-red-50 text-red-700" :
    "border-gray-200 bg-gray-50 text-gray-600";
  element.className = `rounded-md border px-3 py-2 text-sm ${toneClasses}`;
  element.textContent = message;
}

function renderTypeFilters(modal, selectedTypeIds = []) {
  const root = modal.querySelector("[data-labs-patient-type-filters]");
  if (!root) {return;}
  const typeOptions = getPatientTypeConfig()
    .filter((entry) => entry?.id && entry?.isActive !== false)
    .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999) ||
      String(a.name || a.id).localeCompare(String(b.name || b.id)));

  if (typeOptions.length === 0) {
    root.innerHTML = "";
    return;
  }

  const selected = new Set(selectedTypeIds.map(String));
  root.innerHTML = `
    <div class="text-sm">
      <div class="mb-2 font-medium text-gray-700">Patient Type</div>
      <div class="flex flex-wrap gap-2">
        ${typeOptions.map((type) => `
          <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
            <input type="checkbox" value="${attrValue(type.id)}" class="${NATIVE_CHECKBOX_CLASSES} labs-patient-type-filter" ${selected.has(String(type.id)) ? "checked" : ""}>
            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
              <span class="${NATIVE_CHECKBOX_ICON_CLASSES}" data-native-checkbox-icon>check</span>
            </span>
            <span>${sanitizeHTML(type.name || type.id)}</span>
          </label>
        `).join("")}
      </div>
    </div>
  `;
}

function getSelectedTypeIds(modal) {
  return Array.from(modal.querySelectorAll(".labs-patient-type-filter:checked"))
    .map((input) => input.value)
    .filter(Boolean);
}

function renderTypeBadges(typeIds = []) {
  const ids = Array.isArray(typeIds) ? typeIds : [];
  if (ids.length === 0) {return "";}
  return `
    <div class="mt-2 flex flex-wrap gap-1">
      ${ids.map((typeId) => `
        <span class="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
          ${sanitizeHTML(getPatientTypeName(typeId))}
        </span>
      `).join("")}
    </div>
  `;
}

function getPatientContextText(patient = {}) {
  return [
    patient.case_file_number ? `Case ${patient.case_file_number}` : "",
    patient.card_id ? `Card ${patient.card_id}` : "",
  ].filter(Boolean).join(" · ");
}

function renderPatientResultContent(patient = {}, {isSelected = false, showMeta = true} = {}) {
  const context = showMeta ? getPatientContextText(patient) : "";
  const nameClass = isSelected ?
    "text-[var(--dfc-brand-accent)]" :
    "text-gray-900";
  return `
    <span class="min-w-0">
      <span class="block truncate text-sm font-semibold ${nameClass}">${sanitizeHTML(patient.name || "Unnamed Patient")}</span>
      ${context ? `<span class="mt-1 block text-xs text-gray-500">${sanitizeHTML(context)}</span>` : ""}
      ${showMeta ? renderTypeBadges(patient.types) : ""}
    </span>
  `;
}

function renderResults(modal, results = [], selectedPatientId = "") {
  const root = modal.querySelector("[data-labs-patient-results]");
  if (!root) {return;}

  if (results.length === 0) {
    root.innerHTML = `
      <div class="rounded-md border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
        No active matching patients found.
      </div>
    `;
    return;
  }

  root.innerHTML = results.map((patient) => {
    const isSelected = selectedPatientId && patient.id === selectedPatientId;
    return `
      <button
        type="button"
        class="labs-patient-result flex w-full items-start justify-between gap-3 rounded-md border border-gray-200 bg-white p-3 text-left transition-colors hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[var(--dfc-brand-primary-ring)]"
        data-patient-id="${escapeHTML(patient.id)}"
      >
        ${renderPatientResultContent(patient)}
        ${isSelected ? `
          <span class="inline-flex shrink-0 items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600">
            <span class="material-symbols-outlined text-[14px] leading-none">check</span>
            Linked
          </span>
        ` : ""}
      </button>
    `;
  }).join("");
}

function renderSearchPrompt(modal) {
  const root = modal.querySelector("[data-labs-patient-results]");
  if (!root) {return;}
  root.innerHTML = `
    <div class="rounded-md border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
      Enter at least ${PATIENT_LOOKUP_MIN_CHARS} characters to search patients.
    </div>
  `;
}

/**
 * Renders the combined Patient search/free-text field used by Lab create.
 * @param {object} params Field params.
 * @param {object} params.source Lab item/form state.
 * @param {string} [params.className="md:col-span-2"] Wrapper layout class.
 * @returns {string} Field HTML.
 */
export function renderLabsPatientCreateField({
  className = "md:col-span-2",
  source = {},
} = {}) {
  const display = getLabsPatientDisplay(source);
  return `
    <div class="${FIELD_WRAPPER_CLASSES} ${className}" data-labs-patient-create-field data-native-field-wrapper>
      ${getFieldLabelHtml("Patient Name", true)}
      <div
        class="relative"
        data-labs-patient-create-control
        data-initial-linked-patient-id="${attrValue(display.id)}"
        data-initial-patient-name="${attrValue(display.name || source.patient_name || "")}"
      ></div>
    </div>
  `;
}

function renderCreateResults(menu, results = [], selectedPatientId = "") {
  if (!menu) {return;}
  if (results.length === 0) {
    menu.innerHTML = `
      <div class="px-3 py-3 text-sm text-gray-500">
        No active matching patients found. Press enter to save typed value.
      </div>
    `;
    return;
  }

  menu.innerHTML = results.map((patient) => {
    const isSelected = selectedPatientId && patient.id === selectedPatientId;
    const selectedClasses = isSelected ?
      "font-semibold text-[var(--dfc-brand-accent)]" :
      "text-gray-700";
    return `
      <button
        type="button"
        class="labs-patient-create-result flex w-full items-center justify-between gap-3 rounded-lg px-2.5 py-3 text-left text-base transition-colors hover:bg-gray-50 focus:bg-gray-50 focus:outline-none sm:py-2 sm:text-sm ${selectedClasses}"
        data-patient-id="${escapeHTML(patient.id)}"
        role="option"
        aria-selected="${String(isSelected)}"
      >
        ${renderPatientResultContent(patient, {
          isSelected,
          showMeta: false,
        })}
        ${isSelected ? `
          <span class="material-symbols-outlined ml-auto shrink-0 text-[18px] text-[var(--dfc-brand-accent)]" aria-hidden="true">check</span>
        ` : ""}
      </button>
    `;
  }).join("");
}

function setCreateMenuState(menu, {hidden = false, message = ""} = {}) {
  if (!menu) {return;}
  if (hidden) {
    menu.classList.add("hidden");
    menu.innerHTML = "";
    return;
  }
  menu.classList.remove("hidden");
  if (message) {
    menu.innerHTML = `<div class="px-3 py-3 text-sm text-gray-500">${sanitizeHTML(message)}</div>`;
  }
}

/**
 * Mounts the Lab create Patient field as a single search/free-text input.
 * @param {object} params Mount params.
 * @param {HTMLElement} params.root Control root.
 * @param {object} params.formItemData Mutable Labs form state.
 * @param {Function} [params.lookupCandidates] Lookup dependency.
 * @param {Function} [params.onPatientSelected] Selection callback.
 * @returns {void}
 */
export function mountLabsPatientCreateControl({
  root,
  formItemData,
  lookupCandidates = lookupLabsPatientCandidates,
  onPatientSelected,
} = {}) {
  if (!root) {return;}

  const initialPatientId = root.dataset.initialLinkedPatientId || "";
  const initialName = root.dataset.initialPatientName || "";
  formItemData.linked_patient_id = formItemData.linked_patient_id || initialPatientId;
  formItemData.patient_name = formItemData.patient_name || initialName;

  root.innerHTML = `
    <input type="hidden" name="linked_patient_id" value="${attrValue(formItemData.linked_patient_id || "")}">
    <input
      type="text"
      name="patient_name"
      value="${attrValue(formItemData.patient_name || "")}"
      class="${NATIVE_INPUT_CLASSES}"
      autocomplete="off"
      aria-autocomplete="list"
      aria-expanded="false"
      required
    >
    <div
      class="absolute left-0 right-0 top-full z-30 mt-1 hidden max-h-72 overflow-y-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl"
      role="listbox"
      data-labs-patient-create-results
    ></div>
  `;

  const idInput = root.querySelector('input[name="linked_patient_id"]');
  const nameInput = root.querySelector('input[name="patient_name"]');
  const menu = root.querySelector("[data-labs-patient-create-results]");
  let selectedPatientName = formItemData.linked_patient_id ?
    formItemData.patient_name || "" :
    "";
  let latestResults = [];
  let searchTimer = null;
  let requestId = 0;

  const closeMenu = () => {
    setCreateMenuState(menu, {hidden: true});
    nameInput?.setAttribute("aria-expanded", "false");
  };

  const openMenu = () => {
    menu?.classList.remove("hidden");
    nameInput?.setAttribute("aria-expanded", "true");
  };

  const clearLinkForTypedValue = (value) => {
    formItemData.patient_name = value;
    if (!formItemData.linked_patient_id) {return;}
    formItemData.linked_patient_id = "";
    formItemData.case_file_number = "";
    formItemData.card_id = "";
    formItemData.linked_patient_types = [];
    selectedPatientName = "";
    if (idInput) {idInput.value = "";}
  };

  const applyPatient = (patient = {}) => {
    formItemData.linked_patient_id = patient.id || "";
    formItemData.patient_name = patient.name || "";
    formItemData.case_file_number = patient.case_file_number || "";
    formItemData.card_id = patient.card_id || "";
    formItemData.linked_patient_types = Array.isArray(patient.types) ?
      patient.types :
      [];
    if (Array.isArray(patient.provider) && patient.provider.length > 0) {
      formItemData.provider = patient.provider;
    }
    selectedPatientName = formItemData.patient_name;
    if (idInput) {idInput.value = formItemData.linked_patient_id;}
    if (nameInput) {nameInput.value = formItemData.patient_name;}
    closeMenu();
    if (typeof onPatientSelected === "function") {
      onPatientSelected(patient);
    }
  };

  const runSearch = async () => {
    const search = String(nameInput?.value || "").trim();
    if (search.length < PATIENT_LOOKUP_MIN_CHARS) {
      closeMenu();
      return;
    }

    const activeRequestId = ++requestId;
    setCreateMenuState(menu, {message: "Searching..."});
    openMenu();
    try {
      const response = await lookupCandidates({
        search,
        typeIds: [],
        limit: CREATE_SEARCH_LIMIT,
      });
      if (activeRequestId !== requestId) {return;}
      latestResults = Array.isArray(response?.results) ? response.results : [];
      renderCreateResults(menu, latestResults, formItemData.linked_patient_id || "");
      openMenu();
    } catch (error) {
      if (activeRequestId !== requestId) {return;}
      latestResults = [];
      setCreateMenuState(menu, {message: getLookupErrorMessage(error)});
      openMenu();
    }
  };

  nameInput?.addEventListener("input", () => {
    const value = nameInput.value || "";
    if (!selectedPatientName || value !== selectedPatientName) {
      clearLinkForTypedValue(value);
    } else {
      formItemData.patient_name = value;
    }
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => {
      void runSearch();
    }, 250);
  });

  nameInput?.addEventListener("focus", () => {
    if (String(nameInput.value || "").trim().length >= PATIENT_LOOKUP_MIN_CHARS) {
      void runSearch();
    }
  });

  nameInput?.addEventListener("blur", () => {
    window.setTimeout(closeMenu, 150);
  });

  menu?.addEventListener("mousedown", (event) => {
    if (event.target.closest(".labs-patient-create-result")) {
      event.preventDefault();
    }
  });

  menu?.addEventListener("click", (event) => {
    const button = event.target.closest(".labs-patient-create-result");
    if (!button) {return;}
    const patient = latestResults.find((entry) =>
      entry.id === button.dataset.patientId);
    if (patient) {applyPatient(patient);}
  });

  bindNativeFieldBehavior(root);
}

function getSelectedPatientResult(selectedPatientId = "", selectedPatient = null) {
  if (!selectedPatientId) {return null;}

  const patient = getKnownPatient(selectedPatientId);
  if (patient) {return patient;}

  const fallback = selectedPatient || {};
  const fallbackName = String(fallback.name || fallback.patient_name || "").trim();
  if (!fallbackName) {return null;}

  return {
    id: selectedPatientId,
    name: fallbackName,
    case_file_number: fallback.caseFileNumber || fallback.case_file_number || "",
    card_id: fallback.cardId || fallback.card_id || "",
    types: Array.isArray(fallback.types) ? fallback.types : [],
    provider: Array.isArray(fallback.provider) ? fallback.provider : [],
  };
}

function getLookupErrorMessage(error) {
  if (error?.code === "permission-denied") {
    return "You do not have permission to look up patients for Labs.";
  }
  if (error?.code === "invalid-argument") {
    return error.message || "The lookup request was not valid.";
  }
  if (error?.code === "unauthenticated") {
    return "Sign in again before looking up patients.";
  }
  return error?.message || "Patient lookup failed. Please try again.";
}

/**
 * Opens the Labs Patient lookup modal.
 * @param {object} params Modal params.
 * @param {string} [params.selectedPatientId] Currently linked Patient id.
 * @param {object|null} [params.selectedPatient] Current linked Patient data.
 * @param {Function} params.onSelect Callback when a result is selected.
 * @returns {void}
 */
export function openLabsPatientLookupModal({
  selectedPatientId = "",
  selectedPatient = null,
  onSelect,
} = {}) {
  const modal = ensureModal();
  const form = modal.querySelector("[data-labs-patient-search-form]");
  const searchInput = modal.querySelector('input[name="patientSearch"]');
  let latestResults = [];
  let searchTimer = null;

  const close = () => {
    modal.classList.add("hidden");
    modal.classList.remove("flex");
  };

  const runSearch = async () => {
    const search = String(searchInput?.value || "").trim();
    const typeIds = getSelectedTypeIds(modal);
    const resultsRoot = modal.querySelector("[data-labs-patient-results]");
    if (search.length < PATIENT_LOOKUP_MIN_CHARS) {
      latestResults = [];
      renderSearchPrompt(modal);
      setModalMessage(modal, "");
      return;
    }

    if (resultsRoot) {
      resultsRoot.innerHTML = `
        <div class="rounded-md border border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
          Searching...
        </div>
      `;
    }
    setModalMessage(modal, "");

    try {
      const response = await lookupLabsPatientCandidates({
        search,
        typeIds,
        limit: RESULT_LIMIT,
      });
      latestResults = Array.isArray(response?.results) ? response.results : [];
      renderResults(modal, latestResults, selectedPatientId);
      if (latestResults.length === 0) {
        setModalMessage(modal, "No active matching patients found.");
      }
    } catch (error) {
      latestResults = [];
      renderResults(modal, [], selectedPatientId);
      setModalMessage(modal, getLookupErrorMessage(error), "error");
    }
  };

  modal.querySelectorAll(".labs-patient-lookup-close").forEach((button) => {
    button.onclick = close;
  });
  form.onsubmit = (event) => {
    event.preventDefault();
    void runSearch();
  };
  if (searchInput) {
    searchInput.oninput = () => {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(() => {
        void runSearch();
      }, 250);
    };
  }

  renderTypeFilters(modal, []);
  bindNativeFieldBehavior(modal);
  modal.querySelectorAll(".labs-patient-type-filter").forEach((input) => {
    input.onchange = () => void runSearch();
  });
  const currentPatient = getSelectedPatientResult(selectedPatientId, selectedPatient);
  if (currentPatient) {
    latestResults = [currentPatient];
    renderResults(modal, latestResults, selectedPatientId);
  } else {
    modal.querySelector("[data-labs-patient-results]").innerHTML = `
      <div class="rounded-md border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
        Search by patient name, case file, card ID, or type.
      </div>
    `;
  }
  setModalMessage(modal, "");
  if (searchInput) {searchInput.value = "";}
  modal.classList.remove("hidden");
  modal.classList.add("flex");
  requestAnimationFrame(() => searchInput?.focus());

  modal.querySelector("[data-labs-patient-results]").onclick = async (event) => {
    const button = event.target.closest(".labs-patient-result");
    if (!button) {return;}
    const patient = latestResults.find((entry) =>
      entry.id === button.dataset.patientId);
    if (!patient || typeof onSelect !== "function") {return;}

    button.disabled = true;
    setModalMessage(modal, "");
    try {
      const result = await onSelect(patient);
      if (result === false) {
        button.disabled = false;
        return;
      }
      close();
    } catch (error) {
      button.disabled = false;
      setModalMessage(
        modal,
        error?.message || "Could not link this patient.",
        "error",
      );
    }
  };
}

/**
 * Attaches shared linked-Patient field action listeners.
 * @param {HTMLElement} container Root container.
 * @param {object} handlers Handler callbacks.
 * @param {Function} [handlers.onLink] Link/change action.
 * @param {Function} [handlers.onOpen] Open linked Patient action.
 * @param {Function} [handlers.onUnlink] Clear linked Patient action.
 * @returns {void}
 */
export function attachLabsPatientLinkFieldListeners(container, handlers = {}) {
  attachLinkedParentFieldListeners(container, {
    [LINKED_PARENT_ACTIONS.LINK]: handlers.onLink,
    [LINKED_PARENT_ACTIONS.OPEN]: handlers.onOpen,
    [LINKED_PARENT_ACTIONS.UNLINK]: handlers.onUnlink,
  });
}
